import type {
  AppUser,
  FieldDefinition,
  ImportDiscovery,
  ImportMapping,
  IssueType,
  Priority,
  Status,
} from '@servicetim/shared';

/** Target lists the mapping tabs pick from — one fetch per project, resolved once by the container. */
export interface MappingTargets {
  fields: FieldDefinition[];
  statuses: Status[];
  priorities: Priority[];
  users: AppUser[];
  issueTypes: IssueType[];
}

const norm = (s: string): string => s.trim().toLowerCase();

const byName = <T>(needle: string, haystack: readonly T[], nameOf: (t: T) => string): T | null =>
  haystack.find((t) => norm(nameOf(t)) === norm(needle)) ?? null;

/**
 * S-09-03's "suggestions": a same-name (case-insensitive) match in the target project, applied
 * once when discovery + the target lists are both loaded, for any source id the admin hasn't
 * already mapped (either from a previously saved mapping, or an edit already made this session).
 * Absent a match, every field/status/issue type/user still gets an explicit default here —
 * "create new" for fields/statuses/issue types (ADR-0020 §4's own auto-create default, just made
 * visible in the picker rather than left as an invisible absence) and `create_imported` for
 * users; priorities have no "create" option in the schema, so an unmatched one defaults to
 * `null` (dropped). Never overwrites an entry already present in `mapping`.
 */
export function withSuggestedDefaults(
  discovery: ImportDiscovery,
  targets: MappingTargets,
  mapping: ImportMapping,
): ImportMapping {
  const fields = { ...mapping.fields };
  for (const f of discovery.fields) {
    if (fields[f.id] !== undefined) continue;
    const match = byName(f.label, targets.fields, (t) => t.label);
    fields[f.id] = match
      ? { fieldKey: match.key }
      : { create: { type: f.type ?? 'text', label: f.label, config: {} } };
  }

  const statuses = { ...mapping.statuses };
  for (const s of discovery.statuses) {
    if (statuses[s.id] !== undefined) continue;
    const match = byName(s.name, targets.statuses, (t) => t.name);
    statuses[s.id] = match
      ? { statusId: match.id }
      : { createInDraft: { name: s.name, category: s.category ?? 'todo' } };
  }

  const priorities = { ...mapping.priorities };
  for (const p of discovery.priorities) {
    if (priorities[p.id] !== undefined) continue;
    const match = byName(p.name, targets.priorities, (t) => t.name);
    priorities[p.id] = match ? { priorityId: match.id } : null;
  }

  const users = { ...mapping.users };
  for (const u of discovery.users) {
    if (!u.email) continue; // ImportMapping.users is keyed by email; nothing to key an emailless one by.
    if (users[u.email] !== undefined) continue;
    const match = targets.users.find((t) => norm(t.email) === norm(u.email as string));
    users[u.email] = match ? { userId: match.id } : 'create_imported';
  }

  const issueTypes = { ...mapping.issueTypes };
  for (const it of discovery.issueTypes) {
    if (issueTypes[it.id] !== undefined) continue;
    const match = byName(it.name, targets.issueTypes, (t) => t.name);
    issueTypes[it.id] = match ? { issueTypeId: match.id } : { create: { name: it.name } };
  }

  return { fields, statuses, priorities, users, issueTypes, options: mapping.options };
}
